import { css } from "@emotion/css"
import omit from "just-omit"
import { useCallback, useContext } from "react"

import Spacer from "@p-ban/common/src/components/layout/Spacer"
import lookup from "@p-ban/common/src/constants/lookup"
import useMutationApi from "@p-ban/common/src/core/api/useMutationApi"
import COGNITIVE_MEDIA from "@p-ban/core/src/seminar/constant/COGNITIVE_MEDIA"
import PARTICIPATE_INDIVIDUAL_CONSULTATIONS from "@p-ban/core/src/seminar/constant/PARTICIPATE_INDIVIDUAL_CONSULTATIONS"
import SEMINAR_CATEGORY from "@p-ban/core/src/seminar/constant/SEMINAR_CATEGORY"
import { holdIndividualConsultations } from "@p-ban/core/src/seminar/logic/SeminarCategoryLogic"
import USER_TYPE from "@p-ban/core/src/user/constant/USER_TYPE"

import Button from "../../../components/mypage/Button/Button"
import MyPageDefinition from "../../../components/mypage/Definition/MyPageDefinition"
import MyPageDefinitionList from "../../../components/mypage/Definition/MyPageDefinitionList"
import MyPageErrorMessages from "../../../components/mypage/MyPageErrorMessages"
import Divider from "../../../components/mypage/layout/Divider"
import MyPageButtonContainer from "../../../components/mypage/layout/MyPageButtonContainer"
import Text from "../../../components/ui/Text/Text"
import MyPageLayout from "../../../layout/MyPage/MyPageLayout"
import useNextRouter from "../../../routes/useNextRouter"
import seminarApi from "../api/seminarApi"
import type { SeminarValues } from "../api/seminarApi"
import SeminarTemplate from "../components/SeminarTemplate"
import { SeminarEntryFormContext } from "../context/SeminarEntryFormContext"
import { requestMessage } from "../logic/SeminarEntryFormatter"
import { formatSeminarDateTime, formatSite } from "../logic/SeminarFormatter"
import type { SeminarEntryFormValues } from "../types/SeminarEntryFormValues"

type Definition = { label: string; description: string }

const createConfirmData = (
  seminar: SeminarValues,
  seminarEntryForm: SeminarEntryFormValues
): {
  seminarDefinition: Definition[]
  entryDefinition: Definition[]
} => {
  const seminarDefinition: Definition[] = []
  seminarDefinition.push({ label: "セミナー名", description: seminar.seminarName })
  seminarDefinition.push({ label: "開催日", description: formatSeminarDateTime(seminar) })
  seminarDefinition.push({ label: "開催場所", description: formatSite(seminar) })

  const entryDefinition: Definition[] = []
  entryDefinition.push({
    label: "個人・法人",
    description: seminarEntryForm.personal ? lookup(USER_TYPE, seminarEntryForm.personal).label : "",
  })
  entryDefinition.push({ label: "氏名", description: `${seminarEntryForm.familyName} ${seminarEntryForm.firstName}` })
  entryDefinition.push({
    label: "氏名（カナ）",
    description: `${seminarEntryForm.familyNameKana} ${seminarEntryForm.firstNameKana}`,
  })
  entryDefinition.push({ label: "所属（会社名・学校名）", description: seminarEntryForm.company })
  entryDefinition.push({ label: "所属部課名・学部・研究室名", description: seminarEntryForm.department })
  entryDefinition.push({ label: "メール", description: seminarEntryForm.email })
  entryDefinition.push({ label: "お電話", description: seminarEntryForm.telNo })
  if (seminar.seminarCategory === SEMINAR_CATEGORY.IntroductionSeminar.value) {
    entryDefinition.push({
      label: "確認したい内容",
      description: requestMessage(seminarEntryForm),
    })
  } else {
    entryDefinition.push({ label: "ご質問・ご要望など", description: seminarEntryForm.requestMessage })
  }
  if (holdIndividualConsultations(seminar.seminarCategory, seminar.onlineSeminar)) {
    const participateIndividualConsultations = seminarEntryForm.participateIndividualConsultations
      ? PARTICIPATE_INDIVIDUAL_CONSULTATIONS[seminarEntryForm.participateIndividualConsultations]
      : undefined
    entryDefinition.push({
      label: "個別相談会への参加予定",
      description: participateIndividualConsultations?.label ?? "",
    })
    entryDefinition.push({
      label: "個別相談内容",
      description: participateIndividualConsultations?.isParticipate ? seminarEntryForm.consultation : "",
    })
  }

  const cognitiveMedia = seminarEntryForm.cognitiveMedia ? COGNITIVE_MEDIA[seminarEntryForm.cognitiveMedia] : undefined
  entryDefinition.push({
    label: "本セミナーを知ったきっかけ",
    description: cognitiveMedia?.label + (cognitiveMedia?.isOther ? `（${seminarEntryForm.cognitiveMediaOther}）` : ""),
  })

  return { seminarDefinition, entryDefinition }
}

const useIndividualEstimateConfirm = () => {
  const { seminar, seminarEntryForm, isLoggedIn } = useContext(SeminarEntryFormContext)

  const router = useNextRouter("/seminar/entry/complete")

  const { isLoading, mutate, allErrorMessages } = useMutationApi(seminarApi.entry, {
    onSuccess() {
      router.push({ keepQuery: true })
    },
  })
  const submit = useCallback(() => {
    const participateIndividualConsultations = seminarEntryForm.participateIndividualConsultations
      ? PARTICIPATE_INDIVIDUAL_CONSULTATIONS[seminarEntryForm.participateIndividualConsultations]
      : null

    const payload = {
      seminarId: seminar.seminarId,
      ...omit(seminarEntryForm, "requestContents"),
      requestMessage: requestMessage(seminarEntryForm),
      cognitiveMediaOther: (
        seminarEntryForm.cognitiveMedia ? COGNITIVE_MEDIA[seminarEntryForm.cognitiveMedia].isOther : false
      )
        ? seminarEntryForm.cognitiveMediaOther
        : "",
      participateIndividualConsultations: participateIndividualConsultations?.isParticipate ?? null,
      consultation: participateIndividualConsultations?.isParticipate ? seminarEntryForm.consultation : "",
    }

    mutate(payload)
  }, [mutate, seminar.seminarId, seminarEntryForm])

  const confirmData = createConfirmData(seminar, seminarEntryForm)

  return {
    confirmData,
    back: () => window.history.back(),
    allErrorMessages,
    isLoading,
    submit,
    isLoggedIn,
  }
}

const SeminarEntryConfirm = () => {
  const { confirmData, back, submit, allErrorMessages, isLoading, isLoggedIn } = useIndividualEstimateConfirm()

  return (
    <MyPageLayout layout={isLoggedIn ? "full" : "simple"}>
      <SeminarTemplate>
        <Spacer size={2} />
        <div className={styles.description}>
          <Text>
            入力内容を再度ご確認いただき、よろしければ下の［セミナー申し込み］ボタンを押してください。
            <br />
            内容を修正する場合は下の［戻る］ボタンを押してください。
          </Text>
        </div>
        <Spacer size={20} />
        <Divider />

        <Spacer size={40} />
        <section className={styles.stack}>
          <h3 className={styles.title}>お申込みセミナー</h3>
          <EntryDefinitionList list={confirmData.seminarDefinition} />
        </section>
        <Spacer size={40} />
        <section className={styles.stack}>
          <h3 className={styles.title}>お申込み内容</h3>
          <EntryDefinitionList list={confirmData.entryDefinition} />
        </section>
        <Spacer size={20} />
        <MyPageErrorMessages alignCenter>{allErrorMessages}</MyPageErrorMessages>
        <Spacer size={20} />
        <MyPageButtonContainer>
          <Button onClick={back} isLoading={isLoading} inverse padding="short">
            戻る
          </Button>
          <Button onClick={submit} isLoading={isLoading} color="orange">
            セミナー申し込み
          </Button>
        </MyPageButtonContainer>
      </SeminarTemplate>
    </MyPageLayout>
  )
}

export default SeminarEntryConfirm

const EntryDefinitionList = ({
  title,
  list,
}: {
  title?: string
  list: { label: string; description: string }[] | null
}) => {
  if (list === null) {
    return null
  }

  return (
    <section>
      {title && <h4 className={styles.h4}>{title}</h4>}
      {title && <Spacer size={8} />}
      <MyPageDefinitionList>
        {list.map(({ label, description }) => (
          <MyPageDefinition key={label} term={label}>
            {description}
          </MyPageDefinition>
        ))}
      </MyPageDefinitionList>
    </section>
  )
}

const styles = {
  title: css`
    font-size: 16px;
  `,
  stack: css`
    display: flex;
    flex-direction: column;
    row-gap: 15px;
  `,
  h4: css`
    font-weight: normal;
  `,
  description: css`
    height: 40px;
  `,
}
